// src/pages/Organizations/OrganizationsPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Button, Chip, Box,
  TextField, InputAdornment, MenuItem, FormControl, InputLabel, Select,
  Pagination, Paper, Avatar, Alert, Divider, GlobalStyles,
} from '@mui/material';
import { Search, LocationOn, Verified, Add, FilterList } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';

const rengigsPalette = {
  forestGreen: '#2E7D32', deepForest: '#1B5E20', mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A', brandLime: '#CDDC39', charcoal: '#1A2A3A',
  softGreen: 'rgba(46,125,50,0.06)', white: '#FFFFFF', offWhite: '#F8F9FA',
  border: '#E1E9F0', mediumGray: '#6B7280',
  gradient: 'linear-gradient(135deg,#2E7D32 0%,#4CAF50 40%,#8BC34A 70%,#CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg,#CDDC39 0%,#8BC34A 40%,#4CAF50 70%,#2E7D32 100%)',
};

const scrollbarStyles = {
  '*::-webkit-scrollbar': { width: '10px', height: '10px' },
  '*::-webkit-scrollbar-track': { background: rengigsPalette.offWhite, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb': { background: rengigsPalette.gradient, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb:hover': { background: rengigsPalette.gradientReverse },
  '*': { scrollbarWidth: 'thin', scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}` },
};

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // ── Redirect immediately if not authenticated ──────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/organizations' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [organizations,   setOrganizations]   = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [industriesLoading,setIndustriesLoading]=useState(true);
  const [searchTerm,      setSearchTerm]      = useState('');
  const [industry,        setIndustry]        = useState('');
  const [orgType,         setOrgType]         = useState('');
  const [page,            setPage]            = useState(1);
  const [totalPages,      setTotalPages]      = useState(1);
  const [industries,      setIndustries]      = useState([]);
  const [error,           setError]           = useState(null);

  const isMounted         = useRef(true);
  const requestInProgress = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    const fetchIndustries = async () => {
      try {
        setIndustriesLoading(true);
        const data = await api.get('/organizations/industries/');
        if (isMounted.current) setIndustries(Array.isArray(data) ? data : (data?.results || []));
      } catch { /* non-fatal */ } finally {
        if (isMounted.current) setIndustriesLoading(false);
      }
    };
    fetchIndustries();

    return () => { isMounted.current = false; api.abortAllRequests(); };
  }, []);

  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated || requestInProgress.current) return;
    try {
      requestInProgress.current = true;
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: page.toString() });
      if (searchTerm) params.set('search', searchTerm);
      if (industry)   params.set('industry', industry);
      if (orgType)    params.set('organization_type', orgType);
      const data = await api.get(`/organizations/?${params}`);
      if (isMounted.current && data) {
        const results = Array.isArray(data) ? data : (data.results || []);
        setOrganizations(results);
        setTotalPages(Math.ceil((data.count || results.length) / 20) || 1);
      }
    } catch (err) {
      if (!isMounted.current) return;
      if (err?.response?.status === 401) {
        navigate('/login', { state: { from: '/organizations' } });
      } else {
        setError('Failed to load organizations. Please try again.');
      }
    } finally {
      if (isMounted.current) setLoading(false);
      requestInProgress.current = false;
    }
  }, [isAuthenticated, page, searchTerm, industry, orgType, navigate]);

  useEffect(() => { if (isAuthenticated) fetchOrganizations(); }, [fetchOrganizations, isAuthenticated]);

  // Return null while redirect is happening
  if (!isAuthenticated) return null;

  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      <Container maxWidth="xl" sx={{ py: 6, bgcolor: rengigsPalette.offWhite, minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: rengigsPalette.charcoal, letterSpacing: '-1px' }}>
              Organizations
            </Typography>
            <Typography variant="body1" sx={{ color: rengigsPalette.mediumGray }}>
              Manage and explore the Rengigs ecosystem
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<Add />}
            onClick={() => navigate('/organizations/create')}
            sx={{
              background: rengigsPalette.gradient, color: 'white', px: 4, py: 1.5,
              borderRadius: '12px', fontWeight: 700, textTransform: 'none', fontSize: '1rem',
              boxShadow: `0 10px 20px -5px ${rengigsPalette.forestGreen}40`, transition: '0.3s',
              '&:hover': { background: rengigsPalette.forestGreen, transform: 'translateY(-2px)', boxShadow: `0 12px 24px -5px ${rengigsPalette.forestGreen}60` },
            }}>
            Add Organization
          </Button>
        </Box>

        <Grid container spacing={4}>
          {/* Filters sidebar */}
          <Grid item xs={12} lg={3}>
            <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', position: 'sticky', top: 20 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FilterList sx={{ mr: 1, color: rengigsPalette.forestGreen }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: rengigsPalette.charcoal }}>Filters</Typography>
              </Box>
              <TextField fullWidth placeholder="Search organizations…" variant="outlined"
                value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: rengigsPalette.forestGreen }} /></InputAdornment> }}
              />
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select value={industry} label="Industry" disabled={industriesLoading}
                    onChange={e => { setIndustry(e.target.value); setPage(1); }} sx={{ borderRadius: '10px' }}>
                    <MenuItem value="">All Industries</MenuItem>
                    {industries.map(ind => <MenuItem key={ind.id} value={ind.id}>{ind.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Organization Type</InputLabel>
                  <Select value={orgType} label="Organization Type"
                    onChange={e => { setOrgType(e.target.value); setPage(1); }} sx={{ borderRadius: '10px' }}>
                    <MenuItem value="">All Types</MenuItem>
                    {['enterprise','sme','startup','nonprofit','government','educational'].map(t => (
                      <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {(searchTerm || industry || orgType) && (
                <Button fullWidth variant="contained" sx={{ mt: 3, bgcolor: rengigsPalette.forestGreen, textTransform: 'none', fontWeight: 600,
                  '&:hover': { bgcolor: rengigsPalette.deepForest, transform: 'translateY(-2px)' } }}
                  onClick={() => { setSearchTerm(''); setIndustry(''); setOrgType(''); setPage(1); }}>
                  Clear All Filters
                </Button>
              )}
            </Paper>
          </Grid>

          {/* Grid */}
          <Grid item xs={12} lg={9}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}
                action={<Button color="inherit" size="small" onClick={fetchOrganizations}>Retry</Button>}>
                {error}
              </Alert>
            )}
            <Grid container spacing={3}>
              {organizations.map(org => (
                <Grid item xs={12} sm={6} md={4} key={org.id}>
                  <Card onClick={() => navigate(`/organizations/${org.id}`)}
                    sx={{ height: '100%', borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                      border: `1px solid ${rengigsPalette.border}`, transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-8px)', borderColor: rengigsPalette.mediumGreen, boxShadow: `0 20px 40px -15px ${rengigsPalette.forestGreen}40` } }}>
                    <Box sx={{ position: 'relative', height: 120, background: rengigsPalette.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ position: 'absolute', bottom: -30, left: 20, p: 0.5, bgcolor: 'white', borderRadius: '12px', boxShadow: 2 }}>
                        <Avatar src={org.logo} sx={{ width: 60, height: 60, borderRadius: '8px' }}>{org.name?.[0]}</Avatar>
                      </Box>
                    </Box>
                    <CardContent sx={{ pt: 5, pb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: rengigsPalette.charcoal }}>{org.name}</Typography>
                        {org.is_verified && <Verified sx={{ color: rengigsPalette.forestGreen, fontSize: 18 }} />}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocationOn sx={{ fontSize: 14, mr: 0.5, color: rengigsPalette.forestGreen }} />
                        {org.city || 'Global'}, {org.country || 'International'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {org.industry_name && (
                          <Chip label={org.industry_name} size="small"
                            sx={{ bgcolor: rengigsPalette.softGreen, color: rengigsPalette.forestGreen, fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                        {org.organization_type && (
                          <Chip label={org.organization_type} size="small" variant="outlined"
                            sx={{ borderColor: rengigsPalette.forestGreen, color: rengigsPalette.forestGreen, fontWeight: 500, fontSize: '0.7rem' }} />
                        )}
                        <Chip label={`${org.member_count || 0} members`} size="small"
                          sx={{ bgcolor: rengigsPalette.offWhite, color: rengigsPalette.mediumGray, fontSize: '0.7rem' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {organizations.length === 0 && !loading && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: rengigsPalette.charcoal }}>No organizations found</Typography>
                <Typography variant="body1" sx={{ color: rengigsPalette.mediumGray, mb: 3 }}>Try adjusting your search or filters</Typography>
                {(searchTerm || industry || orgType) && (
                  <Button variant="contained" onClick={() => { setSearchTerm(''); setIndustry(''); setOrgType(''); setPage(1); }}
                    sx={{ bgcolor: rengigsPalette.forestGreen, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: rengigsPalette.deepForest } }}>
                    Clear Filters
                  </Button>
                )}
              </Box>
            )}

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="large"
                  sx={{ '& .Mui-selected': { background: `${rengigsPalette.gradient} !important`, color: rengigsPalette.white } }} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default OrganizationsPage;